/**
 * Sysadmin Fine-Tuning → Training Data (Phase 5a infrastructure).
 *
 * Curation queue for training_data rows. Phase 5b's auto-collector
 * worker drops rows here filtered by the existing
 * shouldCollectForTraining gate; the sysadmin approves or rejects each
 * row before it's eligible to feed a trainer.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Database,
  RefreshCw,
  CheckCircle2,
  XCircle,
  ShieldAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatTile } from '@/components/ui/stat-tile';
import { PageError } from '@/components/ui/page-error';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useFtTrainingData,
  useApproveFtTrainingData,
  useRejectFtTrainingData,
  type FtTrainingDataRow,
} from '@/lib/use-sysadmin-fine-tuning';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/fine-tuning/training-data')({
  component: FtTrainingDataPage,
});

function FtTrainingDataPage() {
  const { data, isLoading, error, refetch, isFetching } = useFtTrainingData();
  const items = data?.items ?? [];

  const counts = {
    total: items.length,
    approved: items.filter((r) => r.approved).length,
    pending: items.filter((r) => !r.approved).length,
    withPii: items.filter((r) => r.hasPiiRedaction).length,
  };

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <PageError
          title="Failed to load training data"
          message={error.message}
          onRetry={() => refetch()}
          retrying={isFetching}
        />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Database className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Training Data Curation
            </h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="outline" className="text-xs font-mono">
                Phase 5a
              </Badge>
              <span className="text-xs text-muted-foreground">
                Auto-collected query/answer tuples filtered by feedback +
                faithfulness ≥ 0.9 + no PII. Approve before training.
              </span>
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            className={cn('h-3.5 w-3.5 mr-1.5', isFetching && 'animate-spin')}
          />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Total" value={String(counts.total)} icon={Database} tone="default" />
        <StatTile label="Approved" value={String(counts.approved)} icon={CheckCircle2} tone={counts.approved > 0 ? 'success' : 'muted'} />
        <StatTile label="Pending" value={String(counts.pending)} icon={Database} tone={counts.pending > 0 ? 'default' : 'muted'} />
        <StatTile label="PII redactions" value={String(counts.withPii)} icon={ShieldAlert} tone={counts.withPii > 0 ? 'warning' : 'muted'} />
      </div>

      {isLoading && items.length === 0 ? (
        <div className="py-10 text-center text-muted-foreground text-sm animate-pulse">
          Loading training data…
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Database}
          title="No curated rows yet"
          body="The Phase 5b auto-collector worker will populate this queue from tenant messages + audit_logs filtered by feedback/faithfulness gates."
        />
      ) : (
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Tenant</th>
                <th className="px-3 py-2 font-medium">Source</th>
                <th className="px-3 py-2 font-medium">Preview</th>
                <th className="px-3 py-2 font-medium text-right">Faithfulness</th>
                <th className="px-3 py-2 font-medium">PII</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <TrainingDataRowItem key={r.id} row={r} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TrainingDataRowItem({ row }: { row: FtTrainingDataRow }) {
  const approve = useApproveFtTrainingData();
  const reject = useRejectFtTrainingData();

  const preview =
    row.messages.find((m) => m.role === 'user')?.content ?? '(no user message)';

  return (
    <tr className="border-t hover:bg-muted/30 transition-colors">
      <td className="px-3 py-2.5 font-mono text-[11px] text-muted-foreground">
        {row.tenantId.slice(0, 8)}
      </td>
      <td className="px-3 py-2.5">
        <Badge variant="outline" className="text-[10px]">
          {row.source}
        </Badge>
      </td>
      <td className="px-3 py-2.5 max-w-md truncate text-xs" title={preview}>
        {preview.slice(0, 140)}
      </td>
      <td className="px-3 py-2.5 text-right font-mono text-xs">
        {row.faithfulnessScore != null
          ? row.faithfulnessScore.toFixed(3)
          : '—'}
      </td>
      <td className="px-3 py-2.5">
        {row.hasPiiRedaction ? (
          <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-700 dark:text-amber-300">
            redacted
          </Badge>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        )}
      </td>
      <td className="px-3 py-2.5">
        {row.approved ? (
          <Badge variant="secondary" className="text-[10px]">
            Approved
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px]">
            Pending
          </Badge>
        )}
      </td>
      <td className="px-3 py-2.5 text-right">
        <div className="inline-flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => approve.mutate({ id: row.id })}
            disabled={row.approved || row.hasPiiRedaction || approve.isPending}
            title={
              row.hasPiiRedaction
                ? 'Cannot approve rows with PII redaction'
                : row.approved
                  ? 'Already approved'
                  : 'Approve for training'
            }
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              reject.mutate({ id: row.id, reason: 'manual rejection' })
            }
            disabled={reject.isPending}
            title="Reject (delete row)"
          >
            <XCircle className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}
